import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import type { ReactNode } from 'react';
import { StreamChat } from 'stream-chat';
import { Sentry } from '../lib/sentry';
import { getChatToken } from '../api/client';
import { useAuth } from './AuthContext';

interface StreamContextType {
  client: StreamChat | null;
  isReady: boolean;
  error: string | null;
}

const StreamContext = createContext<StreamContextType>({
  client: null,
  isReady: false,
  error: null,
});

export function useStreamChat() {
  return useContext(StreamContext);
}

export function StreamProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [client, setClient] = useState<StreamChat | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientRef = useRef<StreamChat | null>(null);
  const initializingRef = useRef(false);

  const initStream = useCallback(async () => {
    if (!isAuthenticated || !user || initializingRef.current) return;
    initializingRef.current = true;

    try {
      const { token, apiKey, userId } = await getChatToken();

      const chatClient = StreamChat.getInstance(apiKey);
      await chatClient.connectUser({ id: userId }, token);

      Sentry.addBreadcrumb({ category: 'stream', message: 'Stream Chat connected', level: 'info' });

      clientRef.current = chatClient;
      setClient(chatClient);
      setIsReady(true);
      setError(null);
    } catch (err) {
      console.error('[StreamChat] Init failed:', err);
      // Session expiry is expected behavior (e.g. iOS Safari clears sessionStorage on background);
      // don't flood Sentry with these — auth state is already cleared and user will be redirected.
      const isSessionExpired = err instanceof Error && err.message.includes('Session expired');
      if (!isSessionExpired) {
        Sentry.captureException(err, { tags: { component: 'stream', operation: 'connect' } });
      }
      setError(err instanceof Error ? err.message : 'Failed to connect to chat');
    } finally {
      initializingRef.current = false;
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      initStream();
    }

    return () => {
      if (clientRef.current) {
        clientRef.current.disconnectUser().catch(() => {});
        clientRef.current = null;
        setClient(null);
        setIsReady(false);
      }
    };
  }, [isAuthenticated, user?.id]);

  // Clean up on logout
  useEffect(() => {
    if (!isAuthenticated) {
      if (clientRef.current) {
        clientRef.current.disconnectUser().catch(() => {});
        clientRef.current = null;
        setClient(null);
        setIsReady(false);
      }
    }
  }, [isAuthenticated]);

  return (
    <StreamContext.Provider value={{ client, isReady, error }}>
      {children}
    </StreamContext.Provider>
  );
}
